( function ( $ ) {
	/**
	 * SELECTORS is an object containing CSS selector strings used to select elements on the page.
	 * These selectors correspond to various inputs and elements in the ticket booking form.
	 */
	const SELECTORS = {
		dayCount: '#input_4_4', // Selector for the departing station input
		departingStation: '#input_4_10', // Selector for the departing station input
		arrivingStation: '#input_4_11', // Selector for the arriving station input
		ticketType: '#input_4_9', // Selector for the ticket type input
		weeklyTicketCost: '#input_4_16', // Selector for the Weekly ticket cost input
		monthlyTicketCost: '#input_4_17', // Selector for the Monthly ticket cost input
		annualTicketCost: '#input_4_18', // Selector for the Annual ticket cost input
	};

	/**
	 * Savings Calculator class.
	 */
	class SavingsCalculator {
		/**
		 * Constructor for the TripPlanner class.
		 */
		constructor() {
			this.cacheDom();
			this.getEventMapping = this.getEventMapping.bind( this );
			this.attachEventListeners = this.attachEventListeners.bind( this );
			this.handleChange = this.handleChange.bind( this );
			this.updateFields = this.updateFields.bind( this );
			this.calculateTicketCost = this.calculateTicketCost.bind( this );
			this.enforceDayCountMinMax = this.enforceDayCountMinMax.bind( this );
			this.init();
		}

		/**
		 * Cache DOM elements for better performance.
		 */
		cacheDom = () => {
			this.$dayCount = $( SELECTORS.dayCount );
			this.$departingStation = $( SELECTORS.departingStation );
			this.$arrivingStation = $( SELECTORS.arrivingStation );
			this.$ticketType = $( SELECTORS.ticketType );
			this.$weeklyTicketCost = $( SELECTORS.weeklyTicketCost );
			this.$monthlyTicketCost = $( SELECTORS.monthlyTicketCost );
			this.$annualTicketCost = $( SELECTORS.annualTicketCost );
		};

		/**
		 * Initializes the trip planner.
		 */
		init() {
			this.attachEventListeners();
			this.updateFields();
		}

		/**
		 * Generates the event mapping for attaching event listeners.
		 */
		getEventMapping() {
			const { $ticketType, $departingStation, $arrivingStation, $dayCount } = this;
			return [
				{ element: $departingStation, handler: 'handleChange', arg: 'departing' },
				{ element: $arrivingStation, handler: 'handleChange', arg: 'arriving' },
				{ element: $ticketType, handler: 'calculateTicketCost' },
				{ element: $dayCount, handler: 'enforceDayCountMinMax' },
			];
		}

		/**
		 * Attaches change event listeners to the relevant input fields.
		 */
		attachEventListeners() {
			const { getEventMapping } = this;
			getEventMapping().forEach( ( { element, handler, arg, eventType = 'change' } ) => {
				const boundHandler = arg ? this[ handler ].bind( this, arg ) : this[ handler ].bind( this );
				if ( typeof boundHandler === 'function' ) {
					element.on( eventType, boundHandler );
				} else {
					console.error( `Handler function ${ handler } is not defined` ); // eslint-disable-line no-console
				}
			} );
		}

		/**
		 * Handles change event for the station select fields.
		 *
		 * @param {string} stationType - The type of station, either 'departing' or 'arriving'.
		 */
		handleChange( stationType ) {
			this[ `${ stationType }StationId` ] = this[ `$${ stationType }Station` ].val();
			this.updateFields();
			this.calculateTicketCost();
		}

		/**
		 * Updates the departing and arriving select fields to disable the selected option in the other field.
		 */
		updateFields() {
			const { $departingStation, $arrivingStation } = this;
			const departingStationId = $departingStation.val();
			const arrivingStationId = $arrivingStation.val();

			$departingStation
				.find( 'option' )
				.prop( 'disabled', false )
				.filter( ( _, option ) => $( option ).val() === arrivingStationId )
				.prop( 'disabled', true );
			$arrivingStation
				.find( 'option' )
				.prop( 'disabled', false )
				.filter( ( _, option ) => $( option ).val() === departingStationId )
				.prop( 'disabled', true );
		}

		/**
		 * Calculates and updates the ticket cost based on selected options.
		 * Ensures data is valid and secure before proceeding with calculations.
		 */
		calculateTicketCost() {
			const {
				$ticketType,
				getDataAttributeAsInt: getDataAttributeAsInt1,
				$departingStation,
				$arrivingStation,
				$weeklyTicketCost,
				$monthlyTicketCost,
				$annualTicketCost,
			} = this;

			if ( ! $departingStation.val() || ! $arrivingStation.val() ) {
				return;
			}

			const departingCounties = getDataAttributeAsInt1( $departingStation, 'counties' );
			const arrivingCounties = getDataAttributeAsInt1( $arrivingStation, 'counties' );

			if ( isNaN( departingCounties ) || isNaN( arrivingCounties ) ) {
				console.error( 'Invalid counties data for departing or arriving station.' ); // eslint-disable-line no-console
				return;
			}

			const ticketType = $ticketType.val();

			if ( ! ticketType ) {
				console.error( 'Ticket type not selected.' ); // eslint-disable-line no-console
				return;
			}

			const countiesIndex = Math.abs( departingCounties - arrivingCounties );
			const weeklyTicketCost = window.ticketPrices?.weekly?.[ ticketType ]?.[ countiesIndex ];
			const monthlyTicketCost = window.ticketPrices?.monthly?.[ ticketType ]?.[ countiesIndex ];
			const annualTicketCost = window.ticketPrices?.annual?.[ ticketType ]?.[ countiesIndex ];
			if ( weeklyTicketCost !== undefined ) {
				$weeklyTicketCost.val( weeklyTicketCost ).change();
			}
			if ( monthlyTicketCost !== undefined ) {
				$monthlyTicketCost.val( monthlyTicketCost ).change();
			}
			if ( annualTicketCost !== undefined ) {
				$annualTicketCost.val( annualTicketCost ).change();
			}
		}

		/**
		 * Gets a data attribute from the selected option of a jQuery select element and parses it as an integer.
		 * @param {jQuery} $element  - The jQuery select element.
		 * @param {string} attribute - The data attribute to retrieve.
		 * @return {number} - The parsed integer value, or NaN if parsing fails.
		 */
		getDataAttributeAsInt( $element, attribute ) {
			if ( ! ( $element instanceof jQuery ) || typeof attribute !== 'string' ) {
				console.error( 'Invalid arguments passed to getDataAttributeAsInt' ); // eslint-disable-line no-console
				return NaN;
			}

			const selectedOption = $element.find( 'option:selected' );
			if ( selectedOption.length === 0 ) {
				console.error( 'No option selected in element', $element ); // eslint-disable-line no-console
				return NaN;
			}

			const value = selectedOption.data( attribute );
			const intValue = parseInt( value, 10 );

			if ( isNaN( intValue ) ) {
				console.error( `The data attribute "${ attribute }" is not a valid integer in element`, $element ); // eslint-disable-line no-console
				return NaN;
			}

			return intValue;
		}

		/**
		 * Formats a number as a currency string.
		 * @param {number} number - The number to format.
		 * @return {string} - The formatted currency string.
		 */
		formatCurrency( number ) {
			const numericValue = parseFloat( number );
			if ( isNaN( numericValue ) ) {
				console.error( 'Invalid number passed to formatCurrency:', number ); // eslint-disable-line no-console
				return '$0.00'; // or any other default/error value
			}

			return new Intl.NumberFormat( 'en-US', { style: 'currency', currency: 'USD' } ).format( numericValue );
		}

		/**
		 * Enforces the min and max values for the day count input field.
		 */
		enforceDayCountMinMax() {
			const { $dayCount } = this;
			const minDayCount = parseInt( $dayCount.attr( 'min' ), 10 );
			const maxDayCount = parseInt( $dayCount.attr( 'max' ), 10 );

			const dayCountValue = parseInt( $dayCount.val(), 10 );
			if ( dayCountValue < minDayCount ) {
				$dayCount.val( minDayCount );
			} else if ( dayCountValue > maxDayCount ) {
				$dayCount.val( maxDayCount );
			}
		}
	}
	$( document ).on( 'gform_page_loaded, gform_post_render', function () {
		new SavingsCalculator();
		$( '#gform_4_validation_container' ).appendTo( '#gform_wrapper_4 div.gform_heading' ).first();
	} );
} )( jQuery );
